import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosConfig";
import { BiExport } from "react-icons/bi";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../../components/data-table";
import { API_URL } from "../../../constant";
import { useAuth } from "../../../context/auth-context";
import RangeExportModal from "../../../components/admin-export/RangeExportModal";
import {
  buildPayrollStaffCsv,
  buildPayrollStaffRows,
  createPayrollStaffColumns,
  PAYROLL_STAFF_TABLE_LIMIT,
  type PayrollStaffRecord,
  type PayrollStaffRow,
} from "./payrollStaffTable";
import { fetchPayrollStaffForExport } from "./fetchPayrollStaffExport";

const PayrollList = ({ setOpenStaffModal, setStaffDeet, refetch }: any) => {
  const { isAuthenticated } = useAuth();
  const [staffs, setStaffs] = useState<PayrollStaffRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refetchCount, setRefetchCount] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [exportOpen, setExportOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    axiosInstance
      .get(`${API_URL}api/payroll/staff`)
      .then((r: any) => {
        const sorted = Array.isArray(r?.data?.data)
          ? [...r.data.data].sort(
              (a: any, b: any) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
          : [];
        setStaffs(sorted);
      })
      .catch((e: any) => {
        console.log(e?.response?.data?.message || "An error occurred");
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated, refetch, refetchCount]);

  const handleDelete = useCallback(
    (row: PayrollStaffRow) => {
      const id = row.raw._id;
      if (!id || deletingId === id || !isAuthenticated) return;
      setDeletingId(String(id));
      axiosInstance
        .delete(`${API_URL}api/payroll/staff/${id}`)
        .then(() => {
          setDeletingId(null);
          setRefetchCount((prev) => prev + 1);
        })
        .catch((e: any) => {
          setDeletingId(null);
          console.log(e?.response?.data?.message || "An error occurred");
        });
    },
    [deletingId, isAuthenticated]
  );

  const handleView = useCallback(
    (row: PayrollStaffRow) => {
      setStaffDeet(row.raw);
      setOpenStaffModal(true);
    },
    [setStaffDeet, setOpenStaffModal]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(staffs.length / PAYROLL_STAFF_TABLE_LIMIT)
  );

  const columns = useMemo(
    () => createPayrollStaffColumns(handleView, handleDelete, deletingId),
    [handleView, handleDelete, deletingId]
  );

  const rows = useMemo<PayrollStaffRow[]>(
    () => buildPayrollStaffRows(staffs),
    [staffs]
  );

  const pageRows = useMemo(
    () =>
      rows.slice(
        (page - 1) * PAYROLL_STAFF_TABLE_LIMIT,
        page * PAYROLL_STAFF_TABLE_LIMIT
      ),
    [rows, page]
  );

  if (loading && staffs.length === 0) {
    return (
      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  return (
    <div className="w-full pb-10">
      <div className="mb-3 flex items-center justify-end">
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="flex items-center gap-2"
        >
          <BiExport className="text-white" />
          <span className="uppercase font-sora font-semibold mt-1 text-[#869AA9] text-[10px]">
            Export
          </span>
        </button>
      </div>
      <DataTable<PayrollStaffRow>
        columns={columns}
        data={pageRows}
        currentPage={page}
        setCurrentPage={setPage}
        totalPage={totalPages}
        count={staffs.length}
        limit={PAYROLL_STAFF_TABLE_LIMIT}
        onRowClick={handleView}
        emptyContent="No staff on payroll yet."
        className="overflow-hidden rounded-xl border border-[#2C363E]"
        fixedBodyHeight="min(560px, 55vh)"
      />
      <RangeExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Payroll staff"
        fileBasename="payroll-staff"
        fetchRows={(range) => fetchPayrollStaffForExport(range)}
        buildCsv={(rows) => buildPayrollStaffCsv(rows as PayrollStaffRecord[])}
      />
    </div>
  );
};

export default PayrollList;
